import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { employeeApi, departmentApi } from '../../services/api';
import { PageHeader, StatCard, EmptyState, Spinner } from '../../components/shared/index';
import clsx from 'clsx';
import {
  UsersIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  UserMinusIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import TimeOffTab from './TimeOffTab';
import DocumentsTab from './DocumentsTab';
import OnboardingTab from './OnboardingTab';
import OffboardingTab from './OffboardingTab';

const MANAGER_ROLES = ['manager', 'director', 'vp'];

const TABS = [
  { key: 'overview',    label: 'Overview',    icon: BuildingOffice2Icon },
  { key: 'time-off',    label: 'Time Off',    icon: CalendarDaysIcon },
  { key: 'documents',   label: 'Documents',   icon: DocumentTextIcon },
  { key: 'onboarding',  label: 'Onboarding',  icon: AcademicCapIcon },
  { key: 'offboarding', label: 'Offboarding', icon: ArrowRightOnRectangleIcon },
];

function daysBetween(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

function DepartmentTree({ departments, employees }) {
  const byParent = new Map();
  (departments || []).forEach(d => {
    const key = d.parent_id || 'root';
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(d);
  });
  const counts = new Map();
  (employees || []).forEach(e => {
    counts.set(e.department_id, (counts.get(e.department_id) || 0) + 1);
  });

  const render = (parentId, depth) => {
    const children = byParent.get(parentId) || [];
    if (children.length === 0) return null;
    return (
      <ul className={clsx(depth > 0 && 'ml-6 border-l border-gray-100 pl-3')}>
        {children.map(d => (
          <li key={d.id} className="py-1.5">
            <div className="flex items-center justify-between">
              <Link to={`/app/departments/${d.id}`} className="text-sm font-medium text-gray-800 hover:text-blue-600">
                {d.name}
              </Link>
              <span className="text-xs text-gray-400">{counts.get(d.id) || 0} people</span>
            </div>
            {render(d.id, depth + 1)}
          </li>
        ))}
      </ul>
    );
  };

  if ((departments || []).length === 0) {
    return <EmptyState title="No departments" description="Create your first department to start modelling the org chart." />;
  }

  return <div>{render('root', 0) || render(null, 0)}</div>;
}

function OrgChart({ employees }) {
  const byManager = new Map();
  (employees || []).forEach(e => {
    if (!e.manager_id) return;
    if (!byManager.has(e.manager_id)) byManager.set(e.manager_id, []);
    byManager.get(e.manager_id).push(e);
  });
  const empMap = new Map((employees || []).map(e => [e.id, e]));
  const managers = [...byManager.keys()].map(id => empMap.get(id)).filter(Boolean);

  if (managers.length === 0) {
    return <EmptyState title="No manager hierarchy" description="Assign managers to employees to render the org chart." />;
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-2">
      {managers.map(mgr => {
        const reports = byManager.get(mgr.id) || [];
        return (
          <div key={mgr.id} className="card p-4 w-72 shrink-0">
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-100">
              <div className="w-10 h-10 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">{mgr.name[0]}</div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{mgr.name}</p>
                <p className="text-xs text-gray-500 capitalize">{mgr.role?.replace('_',' ')} · {reports.length} reports</p>
              </div>
            </div>
            <ul className="space-y-1.5">
              {reports.slice(0, 8).map(r => (
                <li key={r.id} className="flex items-center justify-between text-sm">
                  <Link to={`/app/employees/${r.id}`} className="truncate text-gray-700 hover:text-blue-600">{r.name}</Link>
                  <span className="text-xs text-gray-400 ml-2 capitalize">{r.band || '—'}</span>
                </li>
              ))}
              {reports.length > 8 && <li className="text-xs text-gray-400">+{reports.length - 8} more</li>}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function OverviewTab() {
  const { data: employees, isLoading: loadingEmp } = useQuery({
    queryKey: ['employees', 'all'],
    queryFn:  () => employeeApi.list().then(r => r.data?.data || r.data),
  });
  const { data: departments, isLoading: loadingDept } = useQuery({
    queryKey: ['departments'],
    queryFn:  () => departmentApi.list().then(r => r.data),
  });

  const stats = useMemo(() => {
    const list = employees || [];
    const headcount = list.filter(e => e.status !== 'terminated').length;
    const managers  = list.filter(e => MANAGER_ROLES.includes(e.role)).length;
    const terminatedRecent = list.filter(e => e.status === 'terminated' && e.termination_date && daysBetween(e.termination_date) !== null && daysBetween(e.termination_date) <= 90).length;
    return {
      headcount,
      departments: (departments || []).length,
      managers,
      terminatedRecent,
    };
  }, [employees, departments]);

  const onboarding = useMemo(() => {
    return (employees || [])
      .filter(e => e.hire_date && daysBetween(e.hire_date) !== null && daysBetween(e.hire_date) <= 30 && e.status !== 'terminated')
      .sort((a, b) => new Date(b.hire_date) - new Date(a.hire_date));
  }, [employees]);

  const offboarding = useMemo(() => {
    return (employees || []).filter(e => e.status === 'terminated').slice(0, 20);
  }, [employees]);

  if (loadingEmp || loadingDept) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total headcount"        value={stats.headcount}        icon={UsersIcon}              color="blue" />
        <StatCard title="Active departments"     value={stats.departments}      icon={BuildingOffice2Icon}    color="purple" />
        <StatCard title="Managers"               value={stats.managers}         icon={UserGroupIcon}          color="green" />
        <StatCard title="Terminated (90d)"       value={stats.terminatedRecent} icon={UserMinusIcon}          color="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="card p-5 lg:col-span-1">
          <h2 className="section-title mb-3">Department tree</h2>
          <DepartmentTree departments={departments} employees={employees} />
        </div>
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="section-title">Org chart</h2>
            <Link to="/app/employees" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
              All employees <ArrowRightIcon className="w-3 h-3" />
            </Link>
          </div>
          <OrgChart employees={employees} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5">
          <h2 className="section-title mb-3">Recent hires (last 30 days)</h2>
          {onboarding.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">No recent hires.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {onboarding.map(e => (
                <li key={e.id} className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">{e.name[0]}</div>
                    <div className="min-w-0">
                      <Link to={`/app/employees/${e.id}`} className="text-sm font-medium text-gray-900 hover:text-blue-600 truncate block">{e.name}</Link>
                      <p className="text-xs text-gray-500 capitalize">{e.role?.replace('_',' ')} · {e.department?.name || '—'}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap ml-3">Day {daysBetween(e.hire_date)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card p-5">
          <h2 className="section-title mb-3">Terminated employees</h2>
          {offboarding.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">No terminations on file.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {offboarding.map(e => (
                <li key={e.id} className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 bg-red-100 text-red-700 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">{e.name[0]}</div>
                    <div className="min-w-0">
                      <Link to={`/app/employees/${e.id}`} className="text-sm font-medium text-gray-900 hover:text-blue-600 truncate block">{e.name}</Link>
                      <p className="text-xs text-gray-500">{e.termination_date ? `Terminated ${new Date(e.termination_date).toLocaleDateString()}` : 'Terminated'}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HrisHome() {
  const [search, setSearch] = useSearchParams();
  const active = search.get('tab') || 'overview';
  const setActive = (k) => {
    const next = new URLSearchParams(search);
    if (k === 'overview') next.delete('tab');
    else next.set('tab', k);
    setSearch(next, { replace: true });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="People operations"
        subtitle="Headcount, org structure, time-off, documents, onboarding and offboarding."
      />

      <div className="flex gap-1 mb-6 border-b border-gray-200 overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActive(key)}
            className={clsx(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2 whitespace-nowrap',
              active === key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {active === 'overview'    && <OverviewTab />}
      {active === 'time-off'    && <TimeOffTab />}
      {active === 'documents'   && <DocumentsTab />}
      {active === 'onboarding'  && <OnboardingTab />}
      {active === 'offboarding' && <OffboardingTab />}
    </div>
  );
}
